import Text from 'components/_UI/Text';
import currency from 'helpers/currency';
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row'
import { Calendar, Crown, Edit, MoreVertical, Store, Trash2 } from 'lucide-react';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { useUser } from 'context/user/User';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import roles from 'helpers/roles';

const STATUS_LABELS = {
    'disponível': 'Disponível',
    esgotado: 'Esgotado',
    pausado: 'Pausado',
};

function calculateStatus(batch) {
    if (!batch) return false;

    if (Number(batch.quantity_available) === 0) return 'esgotado';
    if (Number(batch.is_active) !== 1) return 'pausado';

    return 'disponível';
}

function formatQuantityCaption(quantity, { live }) {
    if (quantity === undefined || quantity === null || quantity === '') return null;

    const plural = Number(quantity) !== 1;

    return live
        ? `${quantity} disponíve${plural ? 'is' : 'l'}`
        : `${quantity} unidade${plural ? 's' : ''} cadastrada${plural ? 's' : ''}`;
}

export default function ProductCard({
    data,
    onDropdownAction = () => {},
    dropDownVisible = false,
    onMoreClick = () => {},
    onProductClick = () => false,
    visibleMoreAction = false,
    showLiveStatus = false,
}) {
    const { user, getUser } = useUser()
    const [toggleDropdown, setToggleDropdown] = useState(false);

    useEffect(() => {
        if (!user) {
            getUser()
        }
    }, [])

    function renderActions(e) {
        e.stopPropagation();
        setToggleDropdown(!toggleDropdown)
        onMoreClick()
    }

    function canSellInEstablishment() {
        if (!user) {
            return false
        }

        return user?.role?.id != roles.PRODUCER && user?.establishments.length
    }

    const batch = data?.batches?.[0];
    const status = showLiveStatus ? calculateStatus(batch) : false;
    const price = showLiveStatus && batch ? batch.price : data?.price;
    const quantityCaption = showLiveStatus
        ? (status === 'disponível' ? formatQuantityCaption(batch?.quantity_available, { live: true }) : null)
        : formatQuantityCaption(data?.quantity, { live: false });

    return (
        <Card onClick={(e) => onProductClick(e)}>
            <ImageWrapper>
                {
                    data?.image ? (
                        <StyledImage src={data?.image} alt={data?.name} />
                    ) : (
                        <StyledImage src={'../../assets/images/placeholder-products.png'} alt={data?.name} />
                    )
                }
                <ImageOverlay />

                {
                    status && (
                        <StatusBadge status={status}>{STATUS_LABELS[status]}</StatusBadge>
                    )
                }

                {
                    visibleMoreAction &&
                        <MoreButton onClick={renderActions}>
                            <MoreVertical size={16} color={colors.neutralLight} />
                        </MoreButton>
                }
            </ImageWrapper>

            {(toggleDropdown && dropDownVisible) && (
                <DropDownActions onClick={(e) => e.stopPropagation()}>
                    <StyledActions onClick={() => onDropdownAction('edit')}>
                        <Edit size={16} color={panelAccent(colors.occasionalPurple)} />
                        <Text name='small' color={panelAccent(colors.occasionalPurple)}>Editar</Text>
                    </StyledActions>

                    <StyledActions onClick={() => onDropdownAction('delete')}>
                        <Trash2 size={16} color={colors.danger} />
                        <Text name='small' color={colors.danger}>Excluir</Text>
                    </StyledActions>

                    {
                        !!canSellInEstablishment() &&
                            <StyledActions onClick={() => onDropdownAction('sell_establishment')}>
                                <Store size={16} color={colors.goldDark} />
                                <Text name='small' color={colors.goldDark}>Vender no perfil</Text>
                            </StyledActions>
                    }
                </DropDownActions>
            )}

            <ContentInfo>
                {
                    data?.popular && (
                        <PopularTag>
                            <Crown size={10} />
                            Mais vendido
                        </PopularTag>
                    )
                }

                {
                    data?.event?.name && (
                        <EventTag>
                            <Calendar size={10} />
                            {data.event.name}
                        </EventTag>
                    )
                }

                <Text ellipsisAt={'95%'} type='bold' name='highlight' color={colors.nightDark}>
                    {data.name}
                </Text>

                {
                    data?.description && (
                        <Description>{data.description}</Description>
                    )
                }

                <Footer>
                    <PriceTag status={status}>{currency?.format(parseFloat(price))}</PriceTag>

                    {
                        quantityCaption && (
                            <QuantityCaption>{quantityCaption}</QuantityCaption>
                        )
                    }
                </Footer>
            </ContentInfo>
        </Card>
    )
}

const Card = styled(Column)`
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 2px 8px rgba(0, 0, 0, 0.04);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &:hover img {
        transform: scale(1.06);
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
    background: ${colors.neutralDark};
    flex-shrink: 0;
`;

const StyledImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.45s ease;
`;

const ImageOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.15) 0%, transparent 45%);
    pointer-events: none;
`;

const StatusBadge = styled.span`
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: ${fonts.sizes.mini};
    font-weight: ${fonts.weight.bold};
    text-transform: capitalize;
    letter-spacing: 0.3px;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);

    ${({ status }) => status === 'disponível' && `
        background: rgba(43, 200, 102, 0.18);
        color: ${colors.successDark};
    `}

    ${({ status }) => status === 'esgotado' && `
        background: rgba(231, 80, 90, 0.18);
        color: ${colors.dangerDark};
    `}

    ${({ status }) => status === 'pausado' && `
        background: ${colors.goldLight}30;
        color: ${colors.goldDark};
    `}
`;

const MoreButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    cursor: pointer;
    padding: 0;

    &:hover {
        background: rgba(0, 0, 0, 0.6);
    }
`;

const DropDownActions = styled(Column)`
    display: flex;
    width: 160px;
    overflow: auto;
    position: absolute;
    top: 40px;
    right: 8px;
    z-index: 20;
    align-items: stretch;
    justify-content: flex-start;
    border-radius: 10px;
    background: ${colors.neutralLight};
    min-height: auto;
    padding: 6px;
    max-height: 200px;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const StyledActions = styled(Row)`
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-start;
    padding: 8px 6px;
    border-radius: 6px;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${panelAccentAlpha(colors.occasionalPurple, '0D')};
    }

    & > p {
        font-weight: 600;
        padding-bottom: 0;
    }
`;

const ContentInfo = styled(Column)`
    flex: 1;
    width: 100%;
    padding: 10px 12px 12px;
    gap: 2px;
`;

const PopularTag = styled.span`
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 3px;
    margin-bottom: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${colors.goldLight}22;
    color: ${colors.goldDark};
    font-size: ${fonts.sizes.mini};
    font-weight: ${fonts.weight.bold};
    text-transform: uppercase;
`;

const EventTag = styled.span`
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 3px;
    margin-bottom: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '14')};
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: ${fonts.sizes.mini};
    font-weight: ${fonts.weight.bold};
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Description = styled.p`
    margin: 2px 0 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: auto;
    padding-top: 6px;
`;

const PriceTag = styled.p`
    margin: 0;
    font-size: ${fonts.sizes.highlight};
    font-weight: ${fonts.weight.bold};
    color: ${panelAccent(colors.occasionalPurple)};

    ${({ status }) => status === 'esgotado' && `
        text-decoration: line-through;
        color: ${colors.nightLight};
    `}
`;

const QuantityCaption = styled.span`
    font-size: ${fonts.sizes.mini};
    color: ${colors.night};
`;
